import React from "react";
import { connect, useSelector } from "react-redux";
import { format as formatLocalDate } from "date-fns";
import lodashGet from "lodash/get";

import { BackButton } from "jazasoft";
import PageFooter from "jazasoft/lib/mui/layout/PageFooter";
import ExcelIcon from "mdi-material-ui/FileExcel";
import Report from "../mcro/Report";
import { humanizeNumber, isEmpty, listFromCsv, toFixed } from "../../../utils/helpers";
import { format as formatInTenantTimeZone } from "../../../utils/dateUtils";
import MuiTable from "../../../components/MuiTable";
import { convertTableToCell } from "../../../utils/excelHelper";
import { useTenant, useMutation, useQuery, useDeepMemo, useUnit, useDeepEffect } from "../../../hooks";

const getSearchText = filterValues => {
  const { unitId, productList, season, customerList } = filterValues;
  let searchItems = [];
  if (!isEmpty(unitId)) {
    searchItems.push(`unitId=='${unitId}'`);
  }
  if (!isEmpty(productList)) {
    searchItems.push(`product=in=(${productList.map(e => `'${e.name}'`).join(",")})`);
  }
  if (!isEmpty(season)) {
    searchItems.push(`season=='${season}'`);
  }
  if (!isEmpty(customerList)) {
    searchItems.push(`customer=in=(${customerList.map(e => `'${e.name}'`).join(",")})`);
  }
  return searchItems.join(";");
};

const DailyReport = props => {
  const { start, unitId, productList, season, customerList } = props?.filter;

  const i18nMessage = useSelector(state => state?.i18n?.messages || {});
  const tenant = useTenant();
  // const { utc_offset } = useTime();
  const utc_offset = "+05:30"; // because in backend time is saved IST
  const { allUnitList } = useUnit();
  const [download] = useMutation({ rawResponse: true });

  const date = start ? formatLocalDate(new Date(start), "yyyy-MM-dd") : null;
  const searchText = getSearchText(props.filter || {});
  const { data: rows = [] } = useQuery(
    date && {
      url: "daily-cutting-report",
      params: { fromDate: date, ...(!isEmpty(searchText) && { search: searchText }) }
    }
  );

  const columns = useDeepMemo(() => {
    let cols = [
      {
        dataKey: "date",
        title: "Date",
        render: value => (value ? formatInTenantTimeZone(value, utc_offset, "MMM d, yyyy") : "-")
      },
      { dataKey: "unit", title: "Unit", highlight: true, filterOptions: { filter: true } },
      { dataKey: "poRef", title: "ERP No" },
      { dataKey: "fpo", title: "FPO No", highlight: true },
      { dataKey: "customer", title: "Customer", filterOptions: { filter: true } },
      { dataKey: "season", title: "Season" },
      { dataKey: "product", title: "Product", highlight: true, filterOptions: { filter: true } },
      { dataKey: "styleNo", title: "Style No", highlight: true, filterOptions: { filter: true } },
      { dataKey: "style", title: "Style Name", highlight: true, filterOptions: { filter: true } },
      { dataKey: "color", title: "Style Color", highlight: true, filterOptions: { filter: true } },
      { dataKey: "part", title: "Part", highlight: true },
      { dataKey: "lotValue", title: "Lot", highlight: true },
      { dataKey: "inseams", title: "inseams", highlight: true },
      { dataKey: "destinations", title: "Destinations", highlight: true },
      { dataKey: "deliveries", title: "deliveries", highlight: true },
      { dataKey: "delModes", title: "Delivery Modes", highlight: true },
      { dataKey: "customerFlowRefs", title: "BPO", highlight: true },
      { dataKey: "flowRefs", title: "Line ID" },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "fpoQty", title: "FPO Qty" },
      { dataKey: "cutQty", title: "Cut Qty" },
      { dataKey: "cumCutQty", title: "Cum. Cut Qty", align: "center" },
      {
        dataKey: "cumCutQtyPercent",
        title: "Cum. Cut Qty (%)",
        align: "center",
        render: value => `${toFixed(value * 100, 1)}%`,
        cellStyle: { dataFormat: "0.0%" }
      },
      { dataKey: "state", title: "Status" }
    ];

    if (rows.every(e => isEmpty(e.destinations))) {
      cols = cols.filter(c => c.dataKey !== "destinations");
    }
    if (rows.every(e => isEmpty(e.inseams))) {
      cols = cols.filter(c => c.dataKey !== "inseams");
    }
    if (rows.every(e => isEmpty(e.deliveries))) {
      cols = cols.filter(c => c.dataKey !== "deliveries");
    }
    if (rows.every(e => isEmpty(e.delModes))) {
      cols = cols.filter(c => c.dataKey !== "delModes");
    }
    if (rows.every(e => isEmpty(e.customerFlowRefs))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRefs");
    }
    if (rows.every(e => isEmpty(e.flowRefs))) {
      cols = cols.filter(c => c.dataKey !== "flowRefs");
    }

    return cols;
  }, [rows, utc_offset]);

  const finalRows = rows.map(row => ({
    ...row,
    cumCutQtyPercent: row?.orderQty > 0 ? toFixed((row.cumCutQty || 0) / row.orderQty, 3) : 0,
    unit: allUnitList?.find(u => u.unitId === row.unitId)?.name || row.unitId,
    inseams: listFromCsv(row.inseams).join(", "),
    destinations: listFromCsv(row.destinations).join(", "),
    deliveries: listFromCsv(row.deliveries).join(", "),
    delModes: listFromCsv(row.delModes).join(", "),
    flowRefs: listFromCsv(row.flowRefs).join(", "),
    customerFlowRefs: listFromCsv(row.customerFlowRefs).join(", ")
  }));

  useDeepEffect(() => {
    if (isEmpty(finalRows)) {
      typeof props.updateTotal === "function" && props.updateTotal(0);
    } else {
      const total = finalRows?.reduce((acc, row) => acc + row?.cutQty, 0);
      typeof props.updateTotal === "function" && props.updateTotal(humanizeNumber(total || 0));
    }
  }, [rows, allUnitList, finalRows]);

  const onExcelDownload = (rows = [], onDownloadComplete) => {
    let finalRows = rows;
    const fileName = `Daily Cutting Report.xlsx`.replaceAll(" ", "");

    const columnList = columns.map((c, idx) => ({
      ...c,
      colNo: idx + 1,
      title: lodashGet(i18nMessage, `resources.report.cutting.${c.dataKey}`, c.title),
      type: c.dataKey === "date" ? "date" : null
    }));

    const rowList = finalRows.map((row, idx) => ({
      rowNo: idx + 1,
      cellList: columnList.map(c => ({ dataKey: c.dataKey, value: row[c.dataKey] }))
    }));

    const maxColNo = columnList?.length;
    let filterColumns = [
      { dataKey: "date", title: "Date" },
      { dataKey: "unit", title: "Unit" },
      { dataKey: "season", title: "Season" },
      { dataKey: "productList", title: "Products" },
      { dataKey: "customerList", title: "Customers" }
    ];

    let filterRows = [
      {
        date: new Date(start).toLocaleDateString("en-GB"),
        unit: unitId || "",
        productList: !isEmpty(productList) ? productList.map(e => e.name).join(",") : "",
        season: season || "",
        customerList: !isEmpty(customerList) ? customerList.map(e => e.name).join(",") : ""
      }
    ];

    const filterCells = convertTableToCell(filterColumns, filterRows, 3, maxColNo);
    const unit = allUnitList.find(e => e.unitId === unitId) || {};
    const cellList = [
      {
        rowNo: 1,
        colNo: 1,
        value: unit?.displayName || unit?.name || tenant?.name || "",
        colEnd: maxColNo,
        cellStyle: { hAlign: "center", border: false, bold: true, fontHeight: 24 },
        cellHeight: 32
      },
      {
        rowNo: 2,
        colNo: 1,
        value: "Daily Cutting Report",
        colEnd: maxColNo,
        cellStyle: { hAlign: "center", border: false, fontHeight: 18 },
        cellHeight: 32
      },
      ...filterCells
    ];

    const options = {
      url: `export/excel`,
      method: "post",
      responseType: "arraybuffer",
      data: [{ sheetNo: 0, sheetName: "Aggregate Cutting Report", excelData: { columnList, rowList, cellList } }],
      params: { filename: fileName }
    };

    download(options, {
      onSuccess: response => {
        try {
          if (response.status === 200) {
            const header = response.headers["content-disposition"];
            var filename = header ? header.match(/filename="(.+)"/)[1] : fileName;

            let blob = new Blob([response.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            let link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = filename;
            link.click();
          }
        } finally {
          onDownloadComplete && onDownloadComplete();
        }
      },
      onFailure: () => onDownloadComplete && onDownloadComplete()
    });
  };

  const onFooterActionClick = (event, action, rows, filterValues, onActionComplete) => {
    if (action === "download") {
      const activeFilterKeys = Object.keys(filterValues || {}).filter(key => !isEmpty(filterValues[key]));
      const rowsToDownload = rows.filter(row => activeFilterKeys.every(key => row[key] === filterValues[key]));

      onExcelDownload(rowsToDownload, () => onActionComplete && onActionComplete(rows));
    }
  };

  return (
    <>
      <div style={{ marginTop: 10 }}>
        <MuiTable
          title="Order"
          columns={columns}
          rows={finalRows}
          searchable={true}
          searchKeys={["poRef", "fpo", "style", "unitId", "flowRefs", "customerFlowRefs", "styleNo"]}
          pageable={true}
          i18nKey="resources.report.cutting"
          footerActions={[
            {
              name: "download",
              tooltip: "Download",
              icon: <ExcelIcon />,
              options: { style: { color: "green" }, variant: "outlined" }
            }
          ]}
          onFooterActionClick={onFooterActionClick}
        />
      </div>

      <PageFooter style={{ margin: "20px" }}>
        <BackButton variant="contained" color="primary" />
      </PageFooter>
    </>
  );
};

const mapStateToProps = state => ({ i18nMessages: state?.i18n?.messages || {} });
const StyledAggregateReconciliationReport = connect(mapStateToProps)(DailyReport);

const filterKeys = ["start", "unit", "season", "product", "customer"];
const DailyCuttingReport = props => (
  <Report title="Daily Cutting Report" filterOnly={true} defaultMonths={0} totalLabel={"Total Cut Qty"} {...props} filterKeys={filterKeys}>
    <StyledAggregateReconciliationReport />
  </Report>
);

export default DailyCuttingReport;
